<template>
  <Teleport to="body">
    <div class="fixed inset-0 z-[140] flex items-end justify-center">
      <div class="absolute inset-0 bg-black/60" @click="close" />

      <div class="relative w-full max-w-[430px] bg-cream rounded-t-3xl shadow-2xl flex flex-col sheet-inset"
           :style="internalMode === 'recognizing' ? 'height: 85vh; font-family: Inter, sans-serif;' : 'max-height: 90vh'">

        <!-- Состояние загрузки (как в PhotoRecognizeDialog) -->
        <template v-if="internalMode === 'recognizing'">
          <div class="flex-1 flex flex-col" style="padding: 24px 24px 28px;">
            <!-- Кнопка закрытия -->
            <div class="flex justify-end shrink-0">
              <button @click="close" class="w-8 h-8 flex items-center justify-center rounded-full text-ink active:bg-line transition-colors">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            <!-- Фото с эффектом сканирования + заголовок -->
            <div class="flex-1 flex flex-col items-center justify-center" style="gap: 32px;">
              <!-- Фото-контейнер со сканом -->
              <div class="relative overflow-hidden" style="width: 220px; height: 220px; border-radius: 28px; box-shadow: 0 10px 30px rgba(0,0,0,0.10);">
                <img v-if="labelPhotoUrl" :src="labelPhotoUrl" class="w-full h-full object-cover" />
                <div v-else class="w-full h-full bg-line" />
                <div class="scan-overlay" />
                <div class="scan-corner" style="top:12px;left:12px;border-top:2px solid rgba(255,255,255,0.9);border-left:2px solid rgba(255,255,255,0.9);" />
                <div class="scan-corner" style="top:12px;right:12px;border-top:2px solid rgba(255,255,255,0.9);border-right:2px solid rgba(255,255,255,0.9);" />
                <div class="scan-corner" style="bottom:12px;left:12px;border-bottom:2px solid rgba(255,255,255,0.9);border-left:2px solid rgba(255,255,255,0.9);" />
                <div class="scan-corner" style="bottom:12px;right:12px;border-bottom:2px solid rgba(255,255,255,0.9);border-right:2px solid rgba(255,255,255,0.9);" />
              </div>

              <!-- Заголовок + подзаголовок -->
              <div class="text-center">
                <p style="font-weight: 700; font-size: var(--fs-22); color: var(--ink); letter-spacing: -0.2px; margin-bottom: 6px;">Распознаю этикетку</p>
                <p style="font-size: var(--fs-13); color: var(--ink-70);">Обычно 3–15 сек</p>
              </div>
            </div>
          </div>
        </template>

        <!-- Остальные режимы: заголовок + контент -->
        <template v-else>

        <!-- Заголовок -->
        <div class="px-5 pt-5 pb-2 shrink-0 flex items-center justify-between">
          <h2 style="font-family: Inter, sans-serif; font-size: var(--fs-20); font-weight: 700; color: var(--ink);">
            {{ headerTitle }}
          </h2>
          <button @click="close" class="w-8 h-8 flex items-center justify-center rounded-full text-ink active:bg-line transition-colors">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div class="flex-1 overflow-y-auto px-5 pb-6">

          <!-- Режим camera: фото этикетки -->
          <div v-if="internalMode === 'idle'" class="flex flex-col gap-4">
            <p class="text-14 text-ink/70 text-center">
              Сфотографируйте этикетку с пищевой ценностью
            </p>
            <PhotoPicker
              button-class="h-12 rounded-xl bg-ink text-15 font-semibold text-cream active:scale-[0.98] transition-all flex items-center justify-center gap-2"
              gallery-class="rounded-xl bg-[#F0EBE2] text-ink active:scale-[0.98] transition-all"
              camera-denied-text="Для съёмки этикетки нужен доступ к камере. Разрешите доступ в настройках браузера."
              @file="onLabelPhoto"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round">
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                <circle cx="12" cy="13" r="4"/>
              </svg>
              Сфотографировать
            </PhotoPicker>

            <button
              @click="internalMode = 'review'"
              class="text-14 text-ink/70 underline text-center"
            >
              Ввести вручную
            </button>

            <p v-if="errorMsg" class="text-14 text-red-500 text-center">{{ errorMsg }}</p>
          </div>

          <!-- Ревью -->
          <div v-if="internalMode === 'review'" class="flex flex-col gap-3">
            <!-- Подсказка -->
            <p v-if="mode !== 'edit'" class="text-14 text-ink/70 leading-snug">
              {{ mode === 'correction'
                ? 'Исправьте данные продукта. Будет создана новая запись с вашими исправлениями'
                : startInReview
                  ? 'Можно сфотографировать этикетку и я заполню форму. Или вписать данные самостоятельно'
                  : headerText
                    ? 'Вижу этикетку продукта, но нужно уточнить, чтобы правильно занести в базу данных'
                    : 'Вижу штрихкод продукта, но не нахожу его в базе данных. Нужно уточнить, чтобы правильно занести в базу' }}
            </p>

            <!-- Кнопка фото этикетки — когда КБЖУ пусты -->
            <PhotoPicker
              v-if="hasNoKbju"
              wrap-class="gap-2 mt-1"
              button-class="h-11 rounded-xl bg-[#F0EBE2] text-14 text-ink font-medium active:scale-[0.98] transition-all flex items-center justify-center gap-2"
              gallery-class="rounded-xl bg-[#F0EBE2] text-ink active:scale-[0.98] transition-all"
              camera-denied-text="Для съёмки этикетки нужен доступ к камере. Разрешите доступ в настройках браузера."
              @file="onLabelPhoto"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round">
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                <circle cx="12" cy="13" r="4"/>
              </svg>
              Сфотографировать этикетку
            </PhotoPicker>

            <!-- Название -->
            <div>
              <label class="text-14 font-semibold text-ink/70 mb-1 block">Название <span class="text-red-400">*</span></label>
              <div class="relative">
                <input
                  v-model="reviewData.name"
                  type="text"
                  class="w-full h-11 px-3 pr-9 rounded-xl bg-white text-15 text-ink placeholder:text-ink/70 outline-none border border-[#E0D9CF] focus:border-ink"
                  placeholder="Название продукта"
                />
                <button
                  v-if="reviewData.name"
                  @click="reviewData.name = ''"
                  class="absolute right-2.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center rounded-full bg-sand/60 text-ink/70 active:bg-sand"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
            </div>

            <!-- Бренд -->
            <div>
              <label class="text-14 font-semibold text-ink/70 mb-1 block">Бренд / Производитель</label>
              <input
                v-model="reviewData.brand"
                type="text"
                class="w-full h-11 px-3 rounded-xl bg-white text-15 text-ink placeholder:text-ink/70 outline-none border border-[#E0D9CF] focus:border-ink"
                placeholder="Производитель"
              />
            </div>

            <!-- КБЖУ на 100г -->
            <div>
              <label class="text-14 font-semibold text-ink/70 mb-1 block">КБЖУ на 100 г <span class="text-red-400">*</span></label>
              <div class="grid grid-cols-4 gap-2">
                <div>
                  <span class="text-13 font-semibold text-ink/70 block mb-0.5">Ккал</span>
                  <input
                    v-model.number="reviewData.kcal"
                    @beforeinput="onBeforeInput"
                    type="text"
                    inputmode="decimal"
                    class="w-full h-10 px-2 rounded-lg text-15 text-ink text-center outline-none border border-[#E0D9CF] focus:border-ink"
                    style="background: rgba(238,153,140,0.7);"
                  />
                </div>
                <div>
                  <span class="text-13 font-semibold text-ink/70 block mb-0.5">Белки</span>
                  <input
                    v-model.number="reviewData.protein"
                    @beforeinput="onBeforeInput"
                    type="text"
                    inputmode="decimal"
                    class="w-full h-10 px-2 rounded-lg text-15 text-ink text-center outline-none border border-[#E0D9CF] focus:border-ink"
                    style="background: rgba(159,194,193,0.7);"
                  />
                </div>
                <div>
                  <span class="text-13 font-semibold text-ink/70 block mb-0.5">Жиры</span>
                  <input
                    v-model.number="reviewData.fat"
                    @beforeinput="onBeforeInput"
                    type="text"
                    inputmode="decimal"
                    class="w-full h-10 px-2 rounded-lg text-15 text-ink text-center outline-none border border-[#E0D9CF] focus:border-ink"
                    style="background: rgba(235,192,109,0.7);"
                  />
                </div>
                <div>
                  <span class="text-13 font-semibold text-ink/70 block mb-0.5">Углеводы</span>
                  <input
                    v-model.number="reviewData.carbs"
                    @beforeinput="onBeforeInput"
                    type="text"
                    inputmode="decimal"
                    class="w-full h-10 px-2 rounded-lg text-15 text-ink text-center outline-none border border-[#E0D9CF] focus:border-ink"
                    style="background: rgba(238,173,119,0.7);"
                  />
                </div>
              </div>

              <!-- Клетчатка. Свёрнута, потому что российская маркировка её не
                   требует и на большинстве упаковок строки нет — поле пустовало
                   бы чаще, чем заполнялось. Раскрывается по клику; если Зоя
                   распознала волокна на этикетке, раскрывается сама. -->
              <button
                v-if="!fiberOpen"
                type="button"
                @click="fiberOpen = true"
                class="mt-2 text-13 font-semibold text-ink/70 underline underline-offset-2"
              >
                Указать клетчатку
              </button>
              <div v-else class="mt-2">
                <span class="text-13 font-semibold text-ink/70 block mb-0.5">
                  Клетчатка <span class="font-normal text-ink/70">— если указана на упаковке</span>
                </span>
                <input
                  v-model.number="reviewData.fiber"
                  @beforeinput="onBeforeInput"
                  type="text"
                  inputmode="decimal"
                  placeholder="г / 100 г"
                  class="w-full h-10 px-2 rounded-lg text-15 text-ink text-center outline-none border border-[#E0D9CF] focus:border-ink placeholder:text-ink placeholder:font-medium"
                  style="background: rgba(166,183,121,0.7);"
                />
                <p class="text-12 text-ink/70 mt-1 leading-snug">
                  Не нашли на упаковке — оставьте пустым, Зоя оценит сама.
                </p>
              </div>
            </div>

            <!-- Состав продукта -->
            <div>
              <label class="text-14 font-semibold text-ink/70 mb-1 block">Состав</label>
              <textarea
                v-model="reviewData.ingredients_text"
                rows="3"
                class="w-full px-3 py-2 rounded-xl bg-white text-15 text-ink placeholder:text-ink/70 outline-none border border-[#E0D9CF] focus:border-ink resize-none"
                placeholder="Состав продукта"
              />
            </div>

            <!-- Сфотографировать штрихкод -->
            <div v-if="showBarcodeScanner && !scannedBarcode && !barcode">
              <PhotoPicker
                button-class="h-11 rounded-xl bg-[#F0EBE2] text-14 text-ink font-medium active:scale-[0.98] transition-all flex items-center justify-center gap-2"
                gallery-class="rounded-xl bg-[#F0EBE2] text-ink active:scale-[0.98] transition-all"
                camera-denied-text="Для съёмки штрихкода нужен доступ к камере. Разрешите доступ в настройках браузера."
                @file="onBarcodePhoto"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round">
                  <rect x="2" y="4" width="20" height="16" rx="2"/>
                  <line x1="6" y1="8" x2="6" y2="16"/><line x1="10" y1="8" x2="10" y2="16"/>
                  <line x1="14" y1="8" x2="14" y2="16"/><line x1="18" y1="8" x2="18" y2="16"/>
                </svg>
                Сфотографировать штрихкод
              </PhotoPicker>
              <p v-if="barcodeScanning" class="text-14 text-ink/70 text-center mt-1">Распознаю штрихкод...</p>
              <p v-if="barcodeError" class="text-14 text-red-500 text-center mt-1">{{ barcodeError }}</p>
            </div>

            <!-- Штрихкод (read-only если есть) -->
            <div v-if="scannedBarcode || barcode">
              <label class="text-14 font-semibold text-ink/70 mb-1 block">Штрихкод</label>
              <p class="text-15 text-ink px-3 py-2.5 bg-[#F0EBE2] rounded-xl">{{ scannedBarcode || barcode }}</p>
            </div>

            <!-- Ошибка -->
            <p v-if="errorMsg" ref="errorEl" role="alert" class="text-14 text-center rounded-xl px-3 py-2.5 leading-snug" style="color: var(--error); background: rgba(194, 65, 65, 0.08);">{{ errorMsg }}</p>
          </div>

          <!-- Сохранение -->
          <div v-if="internalMode === 'saving'" class="flex flex-col items-center gap-4 py-8">
            <div class="w-8 h-8 border-3 border-sand border-t-[#41291A] rounded-full animate-spin" />
            <p class="text-15 text-ink/70">Сохраняю...</p>
          </div>
        </div>

        <!-- Подсказки о сомнительных значениях.
             Не блокируют сохранение: на упаковке бывает всякое, и запрещать
             человеку вписать то, что он видит, нельзя. Но правка, которая не
             сходится по составу, останется личной — так решает сервер. -->
        <div v-if="internalMode === 'review' && hints.length" class="px-5 pt-1 pb-1 shrink-0">
          <div class="rounded-2xl bg-[#FFF4E0] px-4 py-3 flex flex-col gap-1.5">
            <p v-for="(h, i) in hints" :key="i" class="text-13 leading-snug text-[#7A5B12]">{{ h }}</p>
          </div>
        </div>

        <!-- Кнопка сохранения -->
        <div v-if="internalMode === 'review'" class="px-5 pb-8 pt-2 shrink-0">
          <button
            @click="submit"
            :disabled="!canSubmit"
            class="w-full py-3.5 rounded-2xl bg-ink text-cream text-16 font-semibold
                   disabled:opacity-40 active:scale-[0.98] transition-all"
          >
            {{ mode === 'edit' ? 'Сохранить' : mode === 'correction' ? 'Сохранить исправления' : 'Добавить в базу' }}
          </button>
        </div>

        </template>

      </div>
    </div>

  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { markUnsaved, clearUnsaved } from '../services/unsaved.js'
import { saveDraft, clearDraft } from '../services/drafts.js'
import api from '../services/api.js'
import { BARCODE_LABEL_PROMPT_VERSION } from '../utils/promptVersions'
import { plausibilityHints } from '../utils/plausibility.js'
import { startNetProbe } from '../utils/netdiag.js'
import { withAiJob } from '../utils/aiProgress.js'
import { useAuthStore } from '../store/auth.js'
import { track } from '../analytics.js'
import PhotoPicker from './PhotoPicker.vue'

const authStore = useAuthStore()

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  barcode: { type: String, default: null },
  initialData: { type: Object, default: null },
  headerText: { type: String, default: null },
  showBarcodeScanner: { type: Boolean, default: false },
  mode: { type: String, default: 'create' },     // 'create' | 'edit' | 'correction'
  productId: { type: Number, default: null },     // для edit mode
  originalProductId: { type: Number, default: null }, // для correction mode — ID исправляемого продукта
  startInReview: { type: Boolean, default: false }, // пропустить idle, сразу показать форму
})

const emit = defineEmits(['update:modelValue', 'submitted', 'found-existing'])

// internalMode: 'idle' (camera), 'recognizing', 'review', 'saving'
const internalMode = ref('idle')
const errorMsg = ref('')
const errorEl = ref(null)
const labelPhotoUrl = ref(null)

// Barcode scanning
const scannedBarcode = ref(null)
const barcodeError = ref('')
const barcodeScanning = ref(false)

// Поле клетчатки свёрнуто по умолчанию и раскрывается либо по клику,
// либо когда значение пришло с этикетки — прятать распознанное нельзя.
const fiberOpen = ref(false)

const reviewData = ref({
  name: '',
  brand: '',
  kcal: null,
  protein: null,
  fat: null,
  carbs: null,
  fiber: null,
  ingredients_text: '',
})

const headerTitle = computed(() => {
  if (props.headerText) return props.headerText
  if (props.mode === 'edit') return 'Редактирование продукта'
  if (props.startInReview) {
    if (internalMode.value === 'recognizing') return 'Распознавание...'
    if (internalMode.value === 'saving') return 'Сохранение...'
    return 'Добавление нового продукта'
  }
  if (internalMode.value === 'idle') return 'Этикетка продукта'
  if (internalMode.value === 'recognizing') return 'Распознавание...'
  if (internalMode.value === 'review') return 'Проверьте данные'
  if (internalMode.value === 'saving') return 'Сохранение...'
  return 'Добавить продукт'
})

const hasNoKbju = computed(() =>
  reviewData.value.kcal == null && reviewData.value.protein == null &&
  reviewData.value.fat == null && reviewData.value.carbs == null
)

// Подсказки считаются на лету, пока человек печатает: ошибку дешевле поймать
// до отправки, чем откатывать потом. Расчёт общий с сервером — см.
// utils/plausibility.js и lib/plausibility.js.
const hints = computed(() => plausibilityHints({
  name: reviewData.value.name,
  kcal: reviewData.value.kcal,
  protein: reviewData.value.protein,
  fat: reviewData.value.fat,
  carbs: reviewData.value.carbs,
}))

const canSubmit = computed(() => {
  return reviewData.value.name?.trim() &&
    (reviewData.value.kcal != null || reviewData.value.protein != null)
})

// Пока форма открыта, автообновление не перезагружает страницу; черновик
// карточки нового продукта пишется по мере ввода и снимается при закрытии
// (перезагрузка окно не размонтирует, закрытие и отправка — да). Только
// создание: правку и исправление существующего продукта восстанавливать
// некуда, их исходник в базе.
const unsavedKey = Symbol('LabelReviewDialog')
let draftTimer = null
watch(reviewData, () => {
  if (props.mode !== 'create' || internalMode.value !== 'review') return
  const r = reviewData.value
  if (!(r.name || r.brand || r.ingredients_text || r.kcal != null || r.protein != null)) return
  if (draftTimer) clearTimeout(draftTimer)
  draftTimer = setTimeout(() => {
    saveDraft('label', { reviewData: { ...r }, barcode: scannedBarcode.value || props.barcode || null })
  }, 400)
}, { deep: true })
onUnmounted(() => {
  clearUnsaved(unsavedKey)
  if (draftTimer) { clearTimeout(draftTimer); draftTimer = null }
  if (props.mode === 'create') clearDraft('label')
})

onMounted(() => {
  markUnsaved(unsavedKey)
  if (props.initialData) {
    const d = props.initialData
    reviewData.value.name = d.name || ''
    reviewData.value.brand = d.brand || ''
    reviewData.value.ingredients_text = d.ingredients_text || ''
    if (d.per100) {
      reviewData.value.kcal = d.per100.kcal ?? null
      reviewData.value.protein = d.per100.protein ?? null
      reviewData.value.fat = d.per100.fat ?? null
      reviewData.value.carbs = d.per100.carbs ?? null
      reviewData.value.fiber = d.per100.fiber ?? null
      if (reviewData.value.fiber != null) fiberOpen.value = true
    }
    // Только если есть реальные данные — переходим в ревью.
    // Если только штрихкод (всё остальное null) — остаёмся в idle, чтобы пользователь мог сфотографировать этикетку.
    const hasData = d.name || d.brand || d.per100?.kcal != null || d.per100?.protein != null || d.ingredients_text
    if (hasData || props.mode === 'edit') {
      internalMode.value = 'review'
    }
  }
  if (props.startInReview) {
    internalMode.value = 'review'
  }
})

function close() {
  emit('update:modelValue', false)
}

function onBeforeInput(e) {
  if (e.data && e.data.includes(',')) {
    e.preventDefault()
    document.execCommand('insertText', false, e.data.replace(/,/g, '.'))
  }
}

// ── Фото этикетки ──

async function onLabelPhoto(file) {
  if (!file) return
  errorMsg.value = ''

  const wasReview = internalMode.value === 'review'
  internalMode.value = 'recognizing'

  try {
    const base64 = await resizeAndEncode(file, 1024)
    const mime = file.type || 'image/jpeg'
    labelPhotoUrl.value = `data:${mime};base64,${base64}`
    const result = await withAiJob(job => callGeminiLabel(base64, mime, 2, job))

    // Название: всегда обновляем если Gemini вернул product_name
    // (пользователь явно сфотографировал этикетку для получения данных)
    if (result.product_name) reviewData.value.name = result.product_name
    if (!reviewData.value.brand && result.brand) reviewData.value.brand = result.brand
    if (!reviewData.value.ingredients_text && result.ingredients_text) reviewData.value.ingredients_text = result.ingredients_text
    if (result.per100) {
      reviewData.value.kcal = result.per100.kcal ?? reviewData.value.kcal
      reviewData.value.protein = result.per100.protein ?? reviewData.value.protein
      reviewData.value.fat = result.per100.fat ?? reviewData.value.fat
      reviewData.value.carbs = result.per100.carbs ?? reviewData.value.carbs
      reviewData.value.fiber = result.per100.fiber ?? reviewData.value.fiber
      if (reviewData.value.fiber != null) fiberOpen.value = true
    }
    internalMode.value = 'review'
  } catch (err) {
    errorMsg.value = 'Не удалось распознать этикетку. Попробуйте ещё раз.'
    internalMode.value = wasReview ? 'review' : 'idle'
  }
}

// ── Фото штрихкода (распознавание через Gemini) ──

const BARCODE_PROMPT = `На фото — штрихкод продукта. Прочитай цифры под штрихкодом.
Верни СТРОГО JSON (без markdown): {"barcode": "4607034023881"}
Если штрихкод не читаем — {"barcode": null}`

async function onBarcodePhoto(file) {
  if (!file) return
  barcodeError.value = ''
  barcodeScanning.value = true

  try {
    const base64 = await resizeAndEncode(file, 1024)
    const mime = file.type || 'image/jpeg'

    const { data: result } = await withAiJob(job => api.post(`/ai/gemini-proxy?model=gemini-2.5-flash&label=/barcode/recognize&pv=${BARCODE_LABEL_PROMPT_VERSION}&job=${job}`, {
      contents: [{
        parts: [
          { text: BARCODE_PROMPT },
          { inline_data: { mime_type: mime, data: base64 } },
        ],
      }],
    }, { timeout: 60000 }))

    // Расход токенов считает сервер по ответу Gemini (метка — в query
    // запроса выше); здесь только обновление индикатора бюджета.
    authStore.fetchTokenBudget()

    const text = result.candidates?.[0]?.content?.parts?.[0]?.text?.trim() || ''
    const cleaned = text.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim()
    const parsed = JSON.parse(cleaned)

    if (!parsed.barcode) {
      barcodeError.value = 'Штрихкод не распознан. Попробуйте ещё раз.'
      barcodeScanning.value = false
      return
    }

    scannedBarcode.value = parsed.barcode

    // Проверяем в БД
    try {
      const { data } = await api.get(`/products/barcode/${encodeURIComponent(parsed.barcode)}`)
      if (data.product?.id && (data.product.kcal || data.product.protein)) {
        // Найденная запись бывает беднее этикетки: Open Food Facts нередко
        // знает только калорийность. Отдаём распознанные значения вместе с
        // продуктом, чтобы родитель показал их, а не обнулил углеводы.
        emit('found-existing', data.product, {
          kcal:    reviewData.value.kcal,
          protein: reviewData.value.protein,
          fat:     reviewData.value.fat,
          carbs:   reviewData.value.carbs,
          fiber:   reviewData.value.fiber,
        })
        close()
        barcodeScanning.value = false
        return
      }
    } catch { /* not found in DB — continue with manual review */ }
  } catch {
    barcodeError.value = 'Штрихкод не распознан. Попробуйте ещё раз.'
  }
  barcodeScanning.value = false
}

// ── Submit ──

async function submit() {
  if (!canSubmit.value) return
  internalMode.value = 'saving'
  errorMsg.value = ''

  try {
    const payload = {
      barcode: scannedBarcode.value || props.barcode || null,
      name: reviewData.value.name.trim(),
      brand: reviewData.value.brand?.trim() || null,
      per100: {
        kcal: reviewData.value.kcal,
        protein: reviewData.value.protein,
        fat: reviewData.value.fat,
        carbs: reviewData.value.carbs,
        fiber: reviewData.value.fiber,
      },
      ingredients_text: reviewData.value.ingredients_text?.trim() || null,
    }

    let data
    // Без original_product_id сервер коррекцию не примет, а прежний код молча
    // уходил в ветку «создать новый продукт»: пользователь думал, что исправил
    // карточку, а в базе появлялся дубль, и неверные данные оставались.
    if (props.mode === 'correction' && !props.originalProductId) {
      errorMsg.value = 'Не удалось определить исправляемый продукт'
      internalMode.value = 'review'
      return
    }
    if (props.mode === 'correction' && props.originalProductId) {
      // Коррекция: создаём новый продукт + помечаем оригинал
      const corrPayload = { ...payload, original_product_id: props.originalProductId }
      delete corrPayload.barcode  // коррекция создаётся без штрихкода
      ;({ data } = await api.post('/products/correct', corrPayload))
    } else if (props.mode === 'edit' && props.productId) {
      ({ data } = await api.put(`/products/${props.productId}`, payload))
    } else {
      ({ data } = await api.post('/products/user-submit', payload))
    }

    const trackEvent = props.mode === 'correction' ? 'product_corrected'
      : props.mode === 'edit' ? 'product_edited' : 'product_submitted_label'
    track(trackEvent, {
      product_name: reviewData.value.name,
      has_barcode: !!(scannedBarcode.value || props.barcode),
      has_label_data: !!props.initialData,
      ...(props.mode === 'correction' ? { original_product_id: props.originalProductId } : {}),
    })

    emit('submitted', data)
    emit('update:modelValue', false)
  } catch (err) {
    errorMsg.value = err.response?.data?.error || 'Ошибка сохранения'
    internalMode.value = 'review'
    // Причина отказа — в зону видимости: под кнопкой её не замечали
    await nextTick()
    errorEl.value?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }
}

// ── Gemini label recognition ──

async function callGeminiLabel(base64, mimeType, retries = 2, job = '') {
  // Промпт и вызов Gemini живут на бэкенде (POST /api/ai/recognize-label):
  // раньше текст промпта был продублирован в двух компонентах и уезжал
  // в бандл. Здесь остаётся только картинка и обработка ответа.
  const probe = startNetProbe()
  try {
    const { data } = await api.post('/ai/recognize-label', {
      image: base64,
      mime_type: mimeType,
      job,
    }, { timeout: 120000 })

    probe.done()
    if (data.budget?.pct != null) authStore.updateTokenBudget(data.budget)
    return data.result
  } catch (err) {
    // isAxiosError обязателен. Без него сюда попадала любая ошибка нашего же
    // кода, брошенная после успешного ответа: отсутствие err.response читалось
    // как «сети нет». Так опечатка в auth-store (вызов функции, переименованной
    // в другом коммите) три недели выглядела сетевым сбоем, трижды повторяла
    // запрос и жгла токены Gemini на ответах, которые уже пришли.
    const isNetwork = !err.response && err.isAxiosError === true
    const is429 = err.response?.status === 429
    // Сервер отвечает 200 с телом, а сюда ответ не доходит — собрать с
    // устройства то, чего не видно с сервера. Разбор 27 августа 2026.
    // Сообщаем и о сбое сети, и об исключении своего кода: ответа нет ни в
    // том, ни в другом случае, а отличить их можно только по коду в отчете.
    // Повторяем при этом лишь сетевые — см. isNetwork выше.
    if (!err.response) probe.lost('/ai/recognize-label', err, 3 - retries)
    else probe.done()
    if (retries > 0 && (is429 || isNetwork)) {
      await new Promise(r => setTimeout(r, 3000))
      return callGeminiLabel(base64, mimeType, retries - 1, job)
    }
    if (isNetwork) {
      throw new Error('Не дождались ответа — попробуйте ещё раз')
    }
    throw new Error(err.response?.data?.error || err.message || 'Не удалось распознать этикетку')
  }
}

// ── Resize image ──

function resizeAndEncode(file, maxPx) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const url = URL.createObjectURL(file)
    img.onload = () => {
      URL.revokeObjectURL(url)
      const scale = Math.min(1, maxPx / Math.max(img.width, img.height))
      const w = Math.round(img.width * scale)
      const h = Math.round(img.height * scale)
      const canvas = document.createElement('canvas')
      canvas.width = w; canvas.height = h
      canvas.getContext('2d').drawImage(img, 0, 0, w, h)
      const dataUrl = canvas.toDataURL('image/jpeg', 0.85)
      resolve(dataUrl.split(',')[1])
    }
    img.onerror = reject
    img.src = url
  })
}
</script>

<style scoped>
.scan-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, transparent 0%, rgba(201,122,63,0.2) 50%, transparent 100%);
  background-size: 100% 30%;
  background-repeat: no-repeat;
  animation: scan 2.4s ease-in-out infinite;
}
@keyframes scan {
  0%   { background-position: 0 -30%; }
  50%  { background-position: 0 130%; }
  100% { background-position: 0 -30%; }
}
.scan-corner {
  position: absolute;
  width: 18px;
  height: 18px;
}
</style>
